import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { api } from "../../lib/api";
import { preparationApi } from "../../lib/preparationApi";

const SEASON_KEY = "golf-scoring-v2.selected-season";
const TOURNAMENT_KEY = "golf-scoring-v2.selected-tournament";
const ROUND_TOURNAMENT_KEY = "golf-scoring-v2.round-tournament";
const ROUND_SEASON_KEY = "golf-scoring-v2.round-season";

function storedSeason() {
  if (typeof window === "undefined") return 2026;
  const querySeason = Number(new URLSearchParams(window.location.search).get("season") || 0);
  const value = querySeason
    || Number(window.localStorage.getItem(SEASON_KEY) || 0)
    || Number(window.localStorage.getItem(ROUND_SEASON_KEY) || 0)
    || new Date().getFullYear();
  return Number.isFinite(value) ? value : new Date().getFullYear();
}

export function useTournamentPreparation() {
  const [params, setParams] = useSearchParams();
  const [season, setSeasonState] = useState(storedSeason);
  const tournamentId = params.get("tournament") || "";

  const setSeason = (value: number) => {
    setSeasonState(value);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SEASON_KEY, String(value));
      window.localStorage.setItem(ROUND_SEASON_KEY, String(value));
    }
    const next = new URLSearchParams(params);
    next.set("season", String(value));
    next.delete("tournament");
    next.delete("tab");
    setParams(next, { replace: true });
  };

  const setTournamentId = (value: string) => {
    if (typeof window !== "undefined" && value) {
      window.localStorage.setItem(TOURNAMENT_KEY, value);
      window.localStorage.setItem(ROUND_TOURNAMENT_KEY, value);
      window.localStorage.setItem(SEASON_KEY, String(season));
      window.localStorage.setItem(ROUND_SEASON_KEY, String(season));
    }
    const next = new URLSearchParams(params);
    next.set("season", String(season));
    if (value) next.set("tournament", value); else {
      next.delete("tournament");
      next.delete("tab");
    }
    setParams(next, { replace: true });
  };

  const tournaments = useQuery({
    queryKey: ["tournaments", season],
    queryFn: () => api.tournaments(season),
    staleTime: 15_000,
  });

  useEffect(() => {
    const rows = tournaments.data?.tournaments || [];
    if (!tournamentId || !rows.length || rows.some((row) => row.id === tournamentId)) return;
    const next = new URLSearchParams(params);
    next.delete("tournament");
    next.delete("tab");
    next.set("season", String(season));
    setParams(next, { replace: true });
  }, [tournaments.data, tournamentId, season, params, setParams]);

  const preparation = useQuery({
    queryKey: ["tournament-setup", tournamentId],
    queryFn: () => preparationApi.tournamentSetup(tournamentId),
    enabled: Boolean(tournamentId),
    staleTime: 10_000,
  });

  return {
    season,
    setSeason,
    tournamentId,
    setTournamentId,
    tournaments,
    preparation,
    selectedTournament: tournaments.data?.tournaments.find((row) => row.id === tournamentId),
  };
}
